import { computed, reactive, shallowRef, type Ref } from "vue";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import mitt from "mitt";
import api from "@/services/api";
import {
  type DashboardResponse,
  decodeDashboardResponse,
} from "@/services/dashboardResponse";
import { isLaterStamp } from "@/utils/serverStamps";

// Separate from `dashboard.ts` for the reason set out at the top of
// `dashboardAiReview.ts`: these share one invalidate-both-caches success
// path.

/** Who made a revision. `user` is a person in the editor, `mcp` an agent
 * acting for one, `ai_agent` an automatic run, and `system` nobody's edit:
 * a seeded or migrated entry, a date roll, a repair or an admin save. */
export type RevisionActorKind = "user" | "ai_agent" | "mcp" | "system";

export interface DashboardRevision {
  seq: number;
  createdAt: string;
  actor: number | null;
  /** Null for a seeded entry, a deleted author and a system write alike —
   * three things the panel has no reason to tell apart. */
  actorName: string | null;
  actorKind: RevisionActorKind;
  source: string;
  summary: string;
  isCurrent: boolean;
}

/** One hand that has edited the dashboard. A person's own saves and the AI
 * runs made on their behalf are two contributors, not one. */
export interface DashboardContributor {
  actor: number | null;
  /** Null for an automatic run, and for an author who has been deleted. */
  actorName: string | null;
  actorKind: RevisionActorKind;
  lastEditedAt: string;
}

/** The server lists no more revisions than these, so a list this long may
 * have been cut short. It sends one contributor past its cap when there are
 * more. Mirrors `MAX_REVISIONS_LISTED` and `MAX_CONTRIBUTORS_LISTED` in
 * `backend/dashboard/revisions.py`. */
export const MAX_REVISIONS_LISTED = 100;
export const MAX_CONTRIBUTORS_LISTED = 8;
/** How many days back the server looks for contributors. Mirrors
 * `CONTRIBUTORS_WINDOW` in `backend/dashboard/revisions.py`. */
export const CONTRIBUTORS_WINDOW_DAYS = 90;

export interface DashboardRevisionsResponse {
  revisions: DashboardRevision[];
  /** Null while the dashboard has no history at all. */
  currentSeq: number | null;
  /** The oldest revision undo can reach, which the listing may not include.
   * Null while the dashboard has no history at all. */
  oldestSeq: number | null;
  canUndo: boolean;
  canRedo: boolean;
  /** Computed over the whole table rather than folded from `revisions`,
   * which stops at the listing cap. */
  contributors: DashboardContributor[];
}

export function dashboardRevisionsKey(pk: number) {
  return ["dashboardRevisions", { pk }] as const;
}

/** Under `dashboardRevisionsKey`, so invalidating the history refreshes it. */
export function dashboardRevisionStateKey(pk: number) {
  return [...dashboardRevisionsKey(pk), "state"] as const;
}

function fetchRevisions(pk: number): Promise<DashboardRevisionsResponse> {
  return api
    .get(`/dashboards/${pk}/revisions/`)
    .then((response) => response.data);
}

export function useDashboardRevisionsQuery({
  pk,
  enabled,
}: {
  pk: Ref<number>;
  enabled: Ref<boolean>;
}) {
  return reactive(
    useQuery({
      queryKey: computed(() => dashboardRevisionsKey(pk.value)),
      queryFn: () => fetchRevisions(pk.value),
      // Only while the panel is open. The editor's undo and redo buttons
      // read `useDashboardRevisionStateQuery` instead, which every save
      // refreshes without listing the timeline and its contributors.
      enabled,
      // The editor autosaves every couple of seconds, and each save can add
      // or coalesce an entry. Stale-on-arrival is the normal state here.
      staleTime: 0,
    }),
  );
}

/** Where the dashboard sits in its history, without the history itself:
 * what the editor's undo and redo need after every save. */
export interface DashboardRevisionState {
  /** Null while the dashboard has no history at all. */
  currentSeq: number | null;
  oldestSeq: number | null;
  /** Whether a revision lies that way. A move can still find each one
   * there unrestorable, and says so. */
  canUndo: boolean;
  canRedo: boolean;
}

function fetchRevisionState(pk: number): Promise<DashboardRevisionState> {
  return api
    .get(`/dashboards/${pk}/revision-state/`)
    .then((response) => response.data);
}

/** Keyed under `dashboardRevisionsKey`, so whatever refreshes the history
 * (a save, a move, an AI tool) refreshes this too. */
export function useDashboardRevisionStateQuery({
  pk,
  enabled,
}: {
  pk: Ref<number>;
  enabled: Ref<boolean>;
}) {
  return reactive(
    useQuery({
      queryKey: computed(() => dashboardRevisionStateKey(pk.value)),
      queryFn: () => fetchRevisionState(pk.value),
      enabled,
      staleTime: 0,
    }),
  );
}

/** The response shape when a move is refused. The backend sends the same
 * `{error, hint}` the AI-review endpoints send, so the panel shows the
 * reason rather than a generic failure. */
export interface RevisionMoveError {
  error: string;
  hint?: string;
}

type BlockedMoveReason = "save-failed" | "unsavable" | "busy" | "held-by-ai";

const BLOCKED_MOVE_ERRORS: Record<
  BlockedMoveReason,
  Required<RevisionMoveError>
> = {
  "save-failed": {
    error: "Your latest changes could not be saved, so nothing was moved.",
    hint: "Your edits are still in the editor. Try again once they save.",
  },
  unsavable: {
    error: "Your latest changes can't be saved yet, so nothing was moved.",
    hint: "Your edits are still in the editor. Give the dashboard a title, then try again.",
  },
  busy: {
    error: "The dashboard kept changing as it saved, so nothing was moved.",
    hint: "Try again in a moment.",
  },
  "held-by-ai": {
    error: "AI changes hold this dashboard, so nothing was moved.",
    hint: "Edits can't be saved while AI changes hold this dashboard.",
  },
};

/** A move the editor refused to send because it could not save what the
 * user had. Moving anyway would rebuild the grid from the revision and throw
 * that draft away. */
export class UnsavedChangesBlockMove extends Error {
  /** "unsavable" when no save was sent: the draft could not be built into
   * one, which in the editor means the dashboard has no title. "busy" when
   * every save landed but each brought another edit with it. */
  readonly reason: BlockedMoveReason;

  constructor(reason: BlockedMoveReason = "save-failed") {
    super(BLOCKED_MOVE_ERRORS[reason].error);
    this.name = "UnsavedChangesBlockMove";
    this.reason = reason;
  }
}

/** A move the editor dropped before sending it, as the server refuses a
 * stale one: while the saves ahead of it went out, the editor took the
 * server's copy, which somebody changed since, and the step was asked for
 * from what the user saw before. */
export class DashboardChangedBeforeMove extends Error {
  constructor() {
    super("This dashboard changed since you last loaded it.");
    this.name = "DashboardChangedBeforeMove";
  }
}

export function revisionMoveErrorFrom(
  error: unknown,
): RevisionMoveError | null {
  if (error instanceof DashboardChangedBeforeMove) {
    return {
      error: error.message,
      hint: "Nothing was changed. The latest version is loaded now; check it, then try again.",
    };
  }
  if (error instanceof UnsavedChangesBlockMove) {
    return {
      error: error.message,
      hint: BLOCKED_MOVE_ERRORS[error.reason].hint,
    };
  }
  const data = (error as { response?: { data?: unknown } })?.response?.data;
  // A proxy's 502 or a 500 page arrives as an HTML string, no reason to show.
  if (!data || typeof data !== "object") return null;
  if ("error" in data) {
    const { error: reason, hint } = data as { error: unknown; hint?: unknown };
    // A field named `error` comes back as a list of `{message, code}`.
    if (typeof reason === "string") {
      return typeof hint === "string"
        ? { error: reason, hint }
        : { error: reason };
    }
  }
  // The AI lock answers as an API exception, `{detail: {message, code}}`,
  // and a bad request as `{field: [{message, code}]}`: a reason the user
  // can act on all the same.
  const message = messageIn(
    "detail" in data ? (data as { detail: unknown }).detail : data,
  );
  return message ? { error: message } : null;
}

function messageIn(value: unknown): string | null {
  if (typeof value === "string") return value || null;
  if (Array.isArray(value)) {
    for (const item of value) {
      const message = messageIn(item);
      if (message) return message;
    }
    return null;
  }
  if (value && typeof value === "object") {
    const { message } = value as { message?: unknown };
    if (typeof message === "string") return message;
    return messageIn(Object.values(value));
  }
  return null;
}

function conflictCode(error: unknown): unknown {
  const response = (
    error as { response?: { status?: number; data?: { code?: unknown } } }
  )?.response;
  return response?.status === 409 ? response.data?.code : undefined;
}

/** The move was refused because the dashboard changed since the client
 * last read it: somebody else saved, so the step it asked for is not the
 * one it would take now. */
export function isStaleRevisionError(error: unknown): boolean {
  return conflictCode(error) === "stale_revision";
}

/** The write was refused because the dashboard shows an older version, and
 * saving it would replace every newer one. Sent again with `discardNewer`,
 * it goes ahead. */
export function isBehindNewestError(error: unknown): boolean {
  return conflictCode(error) === "behind_newest";
}

/** A revision move landed, whoever triggered it — the history panel, the
 * editor toolbar, a keyboard shortcut. */
export const revisionEvents = mitt<{ moved: DashboardResponse }>();

function useMoveMutation<TVariables extends { expectedModifiedAt?: string }>(
  request: (
    pk: number,
    variables: TVariables,
  ) => Promise<{ data: Parameters<typeof decodeDashboardResponse>[0] }>,
) {
  const queryClient = useQueryClient();
  return reactive(
    useMutation({
      mutationFn: ({ pk, variables }: { pk: number; variables: TVariables }) =>
        request(pk, variables).then((response) =>
          decodeDashboardResponse(response.data),
        ),
      onSuccess: (moved: DashboardResponse) => {
        // The move returns the dashboard it produced, so the grid redraws
        // from it without waiting for the refetch below. A write the server
        // took after the move may have answered first, and then its copy
        // is the one to rebuild from: the refetch brings nothing newer, and
        // the editor's next save would write the move's over it.
        const key = ["dashboard", { pk: moved.pk }];
        const cached = queryClient.getQueryData<DashboardResponse>(key);
        const dashboard =
          cached && isLaterStamp(cached.modifiedAt, moved.modifiedAt)
            ? cached
            : moved;
        // Before the cache writes: a listener that suppresses its own
        // remote-sync watcher for the rebuild has to be in that state
        // before `setQueryData` schedules one.
        revisionEvents.emit("moved", dashboard);
        queryClient.setQueryData<DashboardResponse>(key, dashboard);
        queryClient.invalidateQueries({
          queryKey: ["dashboard", { pk: dashboard.pk }],
        });
        queryClient.invalidateQueries({
          queryKey: dashboardRevisionsKey(dashboard.pk),
        });
        queryClient.invalidateQueries({ queryKey: ["dashboards"] });
      },
      onError: (error, { pk }) => {
        // Somebody saved since: reading the dashboard again lets the editor
        // adopt what is there now, so the next move starts from it.
        if (!isStaleRevisionError(error)) return;
        queryClient.invalidateQueries({ queryKey: ["dashboard", { pk }] });
        queryClient.invalidateQueries({ queryKey: dashboardRevisionsKey(pk) });
      },
    }),
  );
}

// `expectedModifiedAt` is the `modifiedAt` of the dashboard the client
// shows. The server refuses the move with a 409 when somebody saved since,
// rather than stepping back from their save as though it were the client's
// own.
export function useUndoDashboardMutation() {
  return useMoveMutation<{ expectedModifiedAt?: string }>(
    (pk, { expectedModifiedAt }) =>
      api.post(`/dashboards/${pk}/undo/`, { expectedModifiedAt }),
  );
}

export function useRedoDashboardMutation() {
  return useMoveMutation<{ expectedModifiedAt?: string }>(
    (pk, { expectedModifiedAt }) =>
      api.post(`/dashboards/${pk}/redo/`, { expectedModifiedAt }),
  );
}

export function useRestoreRevisionMutation() {
  return useMoveMutation<{ seq: number; expectedModifiedAt?: string }>(
    (pk, { seq, expectedModifiedAt }) =>
      api.post(`/dashboards/${pk}/restore-revision/`, {
        seq,
        expectedModifiedAt,
      }),
  );
}

export type RevisionMove =
  | { kind: "undo" }
  | { kind: "redo" }
  | { kind: "restore"; seq: number };

/** Sending a move, whichever kind. Resolves with the dashboard it landed
 * on; rejects with the request's error. */
export function useRevisionMoveRequests() {
  const undo = useUndoDashboardMutation();
  const redo = useRedoDashboardMutation();
  const restore = useRestoreRevisionMutation();

  /** `expectedModifiedAt` is the `modifiedAt` of the dashboard the caller
   * read or saved last, or undefined to move without the check. */
  function send(
    pk: number,
    move: RevisionMove,
    expectedModifiedAt?: string,
  ): Promise<DashboardResponse> {
    switch (move.kind) {
      case "undo":
        return undo.mutateAsync({ pk, variables: { expectedModifiedAt } });
      case "redo":
        return redo.mutateAsync({ pk, variables: { expectedModifiedAt } });
      case "restore":
        return restore.mutateAsync({
          pk,
          variables: { seq: move.seq, expectedModifiedAt },
        });
    }
  }

  const isPending = computed(
    () => undo.isPending || redo.isPending || restore.isPending,
  );

  return { send, isPending };
}

/** The open view's way of moving through the history: the editor's, or
 * the dashboard view's.
 *
 * The history panel lives in the sidepanel, outside either component tree,
 * yet a move it starts has to go through the view: the view owns the
 * writes that would otherwise land after the move and write the old layout
 * back over it — the editor's autosave, the view's date roll. */
export interface RevisionMover {
  pk: Readonly<Ref<number>>;
  move: (move: RevisionMove) => Promise<void>;
  moving: Readonly<Ref<boolean>>;
  /** Set while the view holds an edit a move would save first. Behind the
   * newest revision that save drops the revisions above, so the Undone
   * entries could no longer be restored. */
  dropsRedo?: Readonly<Ref<boolean>>;
}

const activeMover = shallowRef<RevisionMover | null>(null);

export function registerRevisionMover(mover: RevisionMover): () => void {
  activeMover.value = mover;
  return () => {
    if (activeMover.value === mover) activeMover.value = null;
  };
}

/** The editor's mover for `pk`, or null when no editor has it open. */
export function useRevisionMover(pk: Ref<number>) {
  return computed(() =>
    activeMover.value?.pk.value === pk.value ? activeMover.value : null,
  );
}
